// Small date / clock helpers for the orb rows (local time, no libraries).
import { dateKeyOf, type UseTodayData } from '../../olmps/useTodayData';

export { dateKeyOf };
export const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const keyToDate = (k: string): Date => {
  const [y, m, d] = k.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
export const addDaysKey = (k: string, n: number): string => {
  const d = keyToDate(k);
  d.setDate(d.getDate() + n);
  return dateKeyOf(d);
};
export const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/** "9:00a" / "2:30p" — compact clock for one-line summaries. */
export function clock(d: Date): string {
  const h = d.getHours();
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(d.getMinutes())}${h >= 12 ? 'p' : 'a'}`;
}
/** "9:00 AM" */
export function clockLong(d: Date): string {
  const h = d.getHours();
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(d.getMinutes())} ${h >= 12 ? 'PM' : 'AM'}`;
}
/** METIS stores agenda time as "H:MM AM/PM". */
export function parseAgendaTime(time: string | null | undefined, base: Date): Date | null {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((time ?? '').trim());
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) h += 12;
  const d = new Date(base);
  d.setHours(h, Number(m[2]), 0, 0);
  return d;
}
export const agendaTimeStr = clockLong;
export const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const trunc = (s: string, n: number) => (s.length > n ? `${s.slice(0, Math.max(1, n - 1)).trimEnd()}…` : s);
export const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

type Ev = UseTodayData['calendarEvents'][number];
export interface DayEvent {
  title: string;
  startMin: number;
  endMin: number;
}
const toMin = (s: string | null | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((s ?? '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** Does a KRNOS event occur on `key` (daily / weekly / monthly / yearly repeats, exceptions, end date). */
function occurs(ev: Ev, key: string): boolean {
  if (ev.exceptions && ev.exceptions.includes(key)) return false;
  if (ev.end_date && ev.end_date < key) return false;
  if (ev.date > key) return false;
  if (!ev.repeat || ev.repeat === 'none') return ev.date === key;
  const s = keyToDate(ev.date);
  const d = keyToDate(key);
  if (ev.repeat === 'daily') return true;
  if (ev.repeat === 'weekly') return d.getDay() === s.getDay();
  if (ev.repeat === 'monthly') return d.getDate() === s.getDate();
  if (ev.repeat === 'yearly') return d.getMonth() === s.getMonth() && d.getDate() === s.getDate();
  return false;
}

export function eventsOn(events: Ev[], key: string): DayEvent[] {
  const out: DayEvent[] = [];
  for (const ev of events) {
    if (!ev.title || !occurs(ev, key)) continue;
    const a = toMin(ev.start_time);
    if (a == null) continue;
    const b = toMin(ev.end_time);
    out.push({ title: ev.title, startMin: a, endMin: b != null && b > a ? b : a + 30 });
  }
  return out;
}

export const minOf = (hhmmStr: string): number => toMin(hhmmStr) ?? 0;
